import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

interface Track {
  id: string;
  title: string;
  artist: string;
  audioUrl: string;
  thumbnailUrl?: string;
  duration?: number;
}

interface PlaylistTrack {
  id: string;
  title: string;
  artist?: string;
  audioUrl: string;
  thumbnailUrl?: string;
  duration?: number;
}

interface AudioPlayerContextType {
  currentTrack: Track | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  playlist: PlaylistTrack[] | null;
  currentPlaylistIndex: number | null;
  playTrack: (track: Track, playlist?: PlaylistTrack[], index?: number) => void | Promise<void>;
  pause: () => void;
  resume: () => void | Promise<void>;
  togglePlayPause: () => void | Promise<void>;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;
  next: () => void;
  previous: () => void;
  close: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | undefined>(undefined);
const fallbackContext: AudioPlayerContextType = {
  currentTrack: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  playlist: null,
  currentPlaylistIndex: null,
  playTrack: () => {},
  pause: () => {},
  resume: () => {},
  togglePlayPause: () => {},
  seek: () => {},
  setVolume: () => {},
  next: () => {},
  previous: () => {},
  close: () => {},
};
let hasWarnedMissingProvider = false;

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(1);
  const [playlist, setPlaylist] = useState<PlaylistTrack[] | null>(null);
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentTrackRef = useRef<Track | null>(null);
  const recoveryAttemptsRef = useRef<Record<string, number>>({});

  const playPromiseRef = useRef<Promise<void> | null>(null);

  const tryPlay = async (): Promise<boolean> => {
    if (!audioRef.current) return false;
    try {
      const promise = audioRef.current.play();
      playPromiseRef.current = promise;
      await promise;
      playPromiseRef.current = null;
      setIsPlaying(true);
      return true;
    } catch (error) {
      playPromiseRef.current = null;
      // AbortError = play() interrupted by pause() — benign, not a real failure
      if (error instanceof DOMException && error.name === 'AbortError') {
        return false;
      }
      // NotSupportedError = unsupported audio format — log but don't throw
      if (error instanceof DOMException && error.name === 'NotSupportedError') {
        console.warn('[AudioPlayer] Unsupported audio source:', currentTrackRef.current?.audioUrl);
        setIsPlaying(false);
        return false;
      }
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      return false;
    }
  };

  const normalizeAudioUrl = (url: string | null | undefined) => (url || '').trim();

  useEffect(() => {
    currentTrackRef.current = currentTrack;
  }, [currentTrack]);

  // Initialize audio element
  useEffect(() => {
    const audio = new Audio();
    audio.preload = 'auto';
    audioRef.current = audio;

    audio.addEventListener('timeupdate', () => {
      setCurrentTime(audio.currentTime);
    });

    audio.addEventListener('loadedmetadata', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    });

    audio.addEventListener('durationchange', () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    });

    audio.addEventListener('playing', () => {
      setIsPlaying(true);
    });

    audio.addEventListener('pause', () => {
      setIsPlaying(false);
    });

    audio.addEventListener('error', () => {
      const mediaError = audio.error;
      // MEDIA_ERR_SRC_NOT_SUPPORTED — no point retrying an unsupported source
      if (mediaError && mediaError.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        console.warn('[AudioPlayer] Source not supported:', audio.src);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        return;
      }
      const track = currentTrackRef.current;
      const sourceKey = normalizeAudioUrl(track?.audioUrl);
      if (sourceKey) {
        const attempts = recoveryAttemptsRef.current[sourceKey] || 0;
        if (attempts < 2) {
          recoveryAttemptsRef.current[sourceKey] = attempts + 1;
          audio.load();
          void tryPlay();
          return;
        }
      }
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    });

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Handle track ended event with playlist navigation
  useEffect(() => {
    if (!audioRef.current) return;

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      // Auto-play next track if in a playlist
      if (playlist && currentPlaylistIndex !== null && currentPlaylistIndex < playlist.length - 1) {
        const nextIndex = currentPlaylistIndex + 1;
        const nextTrack = playlist[nextIndex];
        if (nextTrack && nextTrack.audioUrl) {
          setTimeout(() => {
            playTrack({
              id: nextTrack.id,
              title: nextTrack.title || 'Untitled',
              artist: nextTrack.artist || 'AI Music',
              audioUrl: nextTrack.audioUrl,
              thumbnailUrl: nextTrack.thumbnailUrl,
              duration: nextTrack.duration,
            }, playlist, nextIndex);
          }, 500);
        }
      }
    };

    audioRef.current.addEventListener('ended', handleEnded);

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [playlist, currentPlaylistIndex]);

  const playTrack = async (track: Track, playlistTracks?: PlaylistTrack[], index?: number) => {
    if (!audioRef.current) return;
    const normalizedTrackUrl = normalizeAudioUrl(track.audioUrl);
    if (!normalizedTrackUrl) {
      setIsPlaying(false);
      return;
    }

    const currentSrc = audioRef.current.currentSrc || audioRef.current.src;
    const sameSource = currentSrc === normalizedTrackUrl;
    const hasDuration = Number.isFinite(duration) && duration > 0;

    // If same track, just resume
    if (currentTrack?.id === track.id && audioRef.current.src && sameSource && hasDuration) {
      const ok = await tryPlay();
      if (!ok) {
        // Recover from a stale/broken media element by reloading same source.
        audioRef.current.load();
        await tryPlay();
      }
      return;
    }

    // Set playlist context if provided
    if (playlistTracks && index !== undefined) {
      setPlaylist(playlistTracks);
      setCurrentPlaylistIndex(index);
    } else {
      // Clear playlist context if playing a standalone track
      setPlaylist(null);
      setCurrentPlaylistIndex(null);
    }

    // Load new track
    setCurrentTrack(track);
    setCurrentTime(0);
    setDuration(0);
    recoveryAttemptsRef.current[normalizedTrackUrl] = 0;
    audioRef.current.src = normalizedTrackUrl;
    audioRef.current.load();
    await tryPlay();
  };

  const pause = async () => {
    if (audioRef.current) {
      // Wait for any pending play() promise to settle before calling pause()
      // to avoid "play() interrupted by pause()" DOMException
      if (playPromiseRef.current) {
        try { await playPromiseRef.current; } catch { /* handled in tryPlay */ }
      }
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resume = async () => {
    if (audioRef.current && currentTrack) {
      const ok = await tryPlay();
      if (!ok) {
        audioRef.current.load();
        await tryPlay();
      }
    }
  };

  const togglePlayPause = async () => {
    if (isPlaying) {
      pause();
    } else {
      await resume();
    }
  };

  const seek = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const setVolume = (vol: number) => {
    if (audioRef.current) {
      audioRef.current.volume = vol;
      setVolumeState(vol);
    }
  };

  const next = () => {
    if (!playlist || currentPlaylistIndex === null) return;
    
    const nextIndex = currentPlaylistIndex + 1;
    if (nextIndex < playlist.length) {
      const nextTrack = playlist[nextIndex];
      if (nextTrack && nextTrack.audioUrl) {
        playTrack({
          id: nextTrack.id,
          title: nextTrack.title || 'Untitled',
          artist: nextTrack.artist || 'AI Music',
          audioUrl: nextTrack.audioUrl,
          thumbnailUrl: nextTrack.thumbnailUrl,
          duration: nextTrack.duration,
        }, playlist, nextIndex);
      }
    }
  };

  const previous = () => {
    if (!playlist || currentPlaylistIndex === null) return;
    
    const prevIndex = currentPlaylistIndex - 1;
    if (prevIndex >= 0) {
      const prevTrack = playlist[prevIndex];
      if (prevTrack && prevTrack.audioUrl) {
        playTrack({
          id: prevTrack.id,
          title: prevTrack.title || 'Untitled',
          artist: prevTrack.artist || 'AI Music',
          audioUrl: prevTrack.audioUrl,
          thumbnailUrl: prevTrack.thumbnailUrl,
          duration: prevTrack.duration,
        }, playlist, prevIndex);
      }
    }
  };

  const close = async () => {
    if (audioRef.current) {
      if (playPromiseRef.current) {
        try { await playPromiseRef.current; } catch { /* handled in tryPlay */ }
      }
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setCurrentTrack(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setPlaylist(null);
    setCurrentPlaylistIndex(null);
  };

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        isPlaying,
        currentTime,
        duration,
        volume,
        playlist,
        currentPlaylistIndex,
        playTrack,
        pause,
        resume,
        togglePlayPause,
        seek,
        setVolume,
        next,
        previous,
        close,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (context) return context;

  const isProd = import.meta?.env?.MODE === "production";
  if (!isProd) {
    throw new Error('useAudioPlayer must be used within AudioPlayerProvider');
  }

  if (!hasWarnedMissingProvider && typeof window !== "undefined") {
    hasWarnedMissingProvider = true;
    console.warn("[AudioPlayer] Missing AudioPlayerProvider. Audio controls are disabled.");
  }

  return fallbackContext;
}
